import React, { useState, useEffect } from 'react'
import { CachePersistor } from 'apollo3-cache-persist'

import cache from 'client/cache'
import createClient from 'client'
import dx from 'lib/dashx'
import Loader from 'components/loaders/Loader'
import usePersistedState from 'hooks/usePersistedState'
import { DB_USER_REGION_KEY } from 'models/Account'
import { GET_AUTH, setCurrentAccount } from 'client/state/auth'
import { logout } from 'client/methods'

const SCHEMA_VERSION = '6'
const SCHEMA_VERSION_KEY = 'apollo-schema-version'
const SWITCH_ACCOUNT_ID_PARAM = 'switch_account_id'

// Email links carry the recipient's account so they open in the right org, regardless of
// which account the restored cache had active. Runs before the router and any query mount.
function applyAccountFromURL() {
  const url = new URL(window.location.href)
  const accountId = url.searchParams.get(SWITCH_ACCOUNT_ID_PARAM)
  if (!accountId) return

  url.searchParams.delete(SWITCH_ACCOUNT_ID_PARAM)

  const { auth: { currentToken } = {} } =
    cache.readQuery({ query: GET_AUTH }) || {}
  const accountToken = setCurrentAccount(cache, accountId)

  if (accountToken) {
    dx.setIdentity(accountId, accountToken.dashxToken)
  } else if (currentToken) {
    // Signed in, but not to the linked account: pick/log in to it, then return to the link
    url.searchParams.set('referrer', url.pathname)
    url.pathname = '/choose-organization'
  }

  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  )
}

const storage = window.localStorage
const persistor = new CachePersistor({
  cache,
  storage
})

function ClientProvider({ children }) {
  const [apolloClient, setApolloClient] = useState(null)
  const [userRegion] = usePersistedState(DB_USER_REGION_KEY, 'United States')

  // Handle cache purge and local storage changes, specifically for user region
  const onLocalStorageChange = event => {
    if (event.type === 'user-region-change') {
      const newRegion = storage.getItem(DB_USER_REGION_KEY)
      if (newRegion && userRegion !== newRegion) {
        logout(500).then(async () => persistor.purge())
      }
    }
  }

  useEffect(() => {
    window.addEventListener('user-region-change', onLocalStorageChange)

    return () => {
      window.removeEventListener('user-region-change', onLocalStorageChange)
    }
  }, [])

  // Persist the Apollo cache and manage schema version
  async function persistLocalCache() {
    // Create the Apollo client with the current user region
    const client = createClient(userRegion)

    const currentSchemaVersion = storage.getItem(SCHEMA_VERSION_KEY)

    if (currentSchemaVersion === SCHEMA_VERSION) {
      await persistor.restore()
    } else {
      await persistor.purge()
      storage.setItem(SCHEMA_VERSION_KEY, SCHEMA_VERSION)
    }

    applyAccountFromURL()
    setApolloClient(client)
  }

  useEffect(() => {
    if (!apolloClient) {
      persistLocalCache()
    }
  }, [apolloClient])

  if (!apolloClient) {
    return <Loader />
  }

  return children({ apolloClient })
}

export default ClientProvider
